<template>
  <div class="interview-schedule">
    <div class="header">
      <div class="show-all-switch">
        <el-switch
          v-model="showAllSchedule"
          :width="50"
          :inactive-text="$t(`${i18nPrefix}.label.showAll`)"
        >
        </el-switch>
      </div>
    </div>
    <div class="content" ref="scheduleContent" :style="customScheduleContentStyle">
      <div class="schedule-table">
        <el-tooltip
          v-for="schedule in scheduleList"
          class="schedule-item-tooltip"
          placement="top"
          :open-delay="500"
          :disabled="disabledTooltip || hmScheduleList.length === 0"
          :key="schedule.time"
        >
          <div
            class="schedule-item"
            :class="[schedule.isBusy && 'is-busy', getIsSelectedClass(schedule.time)]"
            @click="selectSchedule(schedule)"
          >
            {{ schedule.time }}
          </div>
          <template #content>
            <div class="tooltip-content">
              <div v-for="hm in hmScheduleList" class="tooltip-content-item" :key="hm.email">
                <i class="iconfont iconshijian-mian icon" :class="schedule.busyHmEmailList.includes(hm.email) && 'is-busy'"></i>
                <span class="label">{{ hm.name }}</span>
              </div>
            </div>
          </template>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'

export default {
  props: {
    durationMinutes: {
      type: Number,
      default: 60
    },
    selectedDate: {
      type: String,
      default: null
    },
    selectedDateTimeList: {
      type: Array,
      default: () => ([])
    },
    hmScheduleList: {
      type: Array,
      default: () => ([])
    },
    timeInterval: {
      type: Number,
      default: 30
    },
    disabledTooltip: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      showAllSchedule: false,
      hasScrollBar: false
    }
  },
  computed: {
    scheduleList () {
      const selectedDate = this.selectedDate || moment().format('YYYY-MM-DD')
      const startTime = `${selectedDate} ${this.showAllSchedule ? '00:00' : '09:00'}`
      const endTime = `${selectedDate} ${this.showAllSchedule ? '23:30' : '17:30'}`
      const scheduleList = this.generateCustomScheduleList(startTime, endTime, this.timeInterval)
      return _.filter(scheduleList, schedule => this.showAllSchedule || !schedule.isBusy)
    },
    customScheduleContentStyle () {
      return {
        marginRight: `${this.hasScrollBar ? -4 : 0}px`
      }
    }
  },
  created () {

  },
  mounted () {
  },
  methods: {
    selectSchedule (schedule) {
      this.$emit('selectSchedule', schedule)
    },
    generateCustomScheduleList (start, end, interval) {
      const nowTime = moment()
      const startTime = moment(start)
      const endTime = moment(end)
      const timeArray = []

      const currentTime = startTime.clone()
      while (!currentTime.isAfter(endTime)) {
        if (currentTime.isBefore(nowTime)) {
          currentTime.add(interval, 'minutes')
          continue
        }
        const time = currentTime.format('HH:mm')
        const busyHmEmailList = _.chain(this.hmScheduleList)
          .filter((hmSchedule) => {
            return _.some(hmSchedule.event_list, (busyTime) => {
              return busyTime.status !== 'free' &&
                this.isTimeRangeOverlap(currentTime, currentTime.clone().add(this.durationMinutes, 'minutes'), busyTime.start_time, busyTime.end_time)
            })
          })
          .map((hmSchedule) => hmSchedule.email)
          .value()

        timeArray.push({
          time,
          busyHmEmailList,
          isBusy: !_.isEmpty(busyHmEmailList)
        })
        currentTime.add(interval, 'minutes')
      }

      return timeArray
    },
    isTimeRangeOverlap (start1, end1, start2, end2) {
      start1 = moment(start1)
      end1 = moment(end1)
      start2 = moment(start2)
      end2 = moment(end2)

      return end1.isAfter(start2) && start1.isBefore(end2)
    },
    getIsSelectedClass (time) {
      const currentTime = moment(`${this.selectedDate} ${time}`)
      return _.some(this.selectedDateTimeList, (dateTime) => {
        const selectedTime = moment(dateTime.dateTime)
        return selectedTime.isSameOrAfter(currentTime) && selectedTime.isBefore(currentTime.clone().add(this.timeInterval, 'minutes'))
      }) && 'is-selected'
    }
  },
  watch: {
    scheduleList: {
      immediate: true,
      handler () {
        this.$nextTick(() => {
          this.hasScrollBar = this.$refs.scheduleContent?.scrollHeight > this.$refs.scheduleContent?.clientHeight
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.interview-schedule {
  box-sizing: border-box;
  width: 460px;

  .header {
    text-align: right;

    .show-all-switch {
      .el-switch {
        ::v-deep {
          .el-switch__label {
            margin: 0;
            margin-right: 10px;
            font-weight: 400;

            span {
              color: #999;
              font-size: 12px;
              line-height: 18px;
            }
          }
        }
      }
    }
  }

  .content {
    margin-top: 16px;
    max-height: 174px;
    overflow: hidden auto;

    .schedule-table {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 6px;
      margin: 0 auto;

      .schedule-item {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 110px;
        height: 30px;
        line-height: 20px;
        border: 1px solid rgba(0, 150, 204, 0.3);
        border-radius: 2px;
        box-sizing: border-box;
        font-size: 14px;
        color: #0096cc;
        cursor: pointer;
        transition: background-color 0.25s ease;

        &.is-busy {
          background-color: #e7e7e7;
          border-color: #e7e7e7;
          color: #999;

          &:hover {
            border-color: rgba(0, 150, 204, 0.1);
          }

          &.is-selected {
            border-color: rgba(0, 150, 204, 0.1);
          }
        }

        &.is-selected {
          font-weight: 500;
          background-color: rgba(0, 150, 204, 0.1);
        }
      }
    }

    &::-webkit-scrollbar-thumb {
      background: #c3c5c9;
      border-radius: 4px;
    }

    &::-webkit-scrollbar {
      width: 4px;
      height: 8px;
      background-color: #fff;
    }
  }
}

.tooltip-content {
  .tooltip-content-item {
    .icon {
      color: #98d49a;
      font-size: 14px;
      margin-right: 8px;
      position: relative;

      &.is-busy {
        color: #f1727d;
      }

      &::after {
        content: '';
        width: 6px;
        height: 8px;
        display: inline-block;
        position: absolute;
        top: 2px;
        left: 5px;
        border-radius: 50%;
        background-color: #fff;
      }

      &::before {
        position: relative;
        z-index: 1;
      }
    }

    .label {
      color: #fff;
      font-size: 12px;
      line-height: 18px;
    }
  }
}
</style>
